import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, isPast, isToday } from 'date-fns';
import { Bell, Phone, ListTodo, Pencil, Check, Undo2, Clock, X, ChevronDown } from 'lucide-react';
import clsx from 'clsx';

import { todosAPI, venuesAPI } from '../../api/endpoints';
import { useToastAction } from '../../hooks/useToastAction';
import { useInvalidateTodos } from '../../hooks/useTodos';
import { TodoModal, type TodoFormData } from './TodoModal';
import type { Reminder, Contact, Band, TodoType } from '../../types';

type TodoFilter = 'active' | 'dismissed' | 'done' | 'all';

interface VenueTodosProps {
  venueId: string;
  venueName?: string;
  contacts: Contact[];
  bands: Band[];
  selectedContactId?: string;
  /** Controlled collapse state. When provided the parent owns it — the ContactOverlay does, so
   *  that the "t" shortcut and the header chevron drive the same value. */
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

const TYPE_ICON: Record<TodoType, typeof Bell> = {
  remind: Bell,
  contact: Phone,
  task: ListTodo,
};

const SNOOZE_PRESETS = [
  { label: '1 day', days: 1 },
  { label: '1 week', days: 7 },
  { label: '2 weeks', days: 14 },
  { label: '1 month', days: 30 },
];

const ACTION_BTN =
  'p-1 text-dark-text-muted hover:text-primary-400 hover:bg-dark-border rounded-sm transition-colors flex-shrink-0';

export function VenueTodos({
  venueId,
  venueName,
  contacts,
  bands,
  selectedContactId,
  collapsed: collapsedProp,
  onCollapsedChange,
}: VenueTodosProps) {
  const invalidate = useInvalidateTodos();
  const { run } = useToastAction();

  const [filter, setFilter] = useState<TodoFilter>('active');
  const [localCollapsed, setLocalCollapsed] = useState(false);
  const collapsed = collapsedProp ?? localCollapsed;

  const toggleCollapsed = () => {
    const next = !collapsed;
    if (collapsedProp === undefined) setLocalCollapsed(next);
    onCollapsedChange?.(next);
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState<Reminder | null>(null);

  const { data: todos = [], isLoading } = useQuery({
    queryKey: ['venue-todos', venueId],
    queryFn: () =>
      todosAPI
        .list({ venue_id: venueId, include_dismissed: true, page_size: 200 })
        .then((r) => r.data.items),
    enabled: !!venueId,
  });

  const filtered = useMemo(() => {
    const matches =
      filter === 'all'
        ? todos
        : filter === 'active'
          ? todos.filter((t) => t.status === 'pending')
          : todos.filter((t) => t.status === filter);
    // Active: soonest first (overdue at top). Done/dismissed/all: most recent first.
    return [...matches].sort((a, b) => {
      const da = new Date(a.datetime).getTime();
      const db = new Date(b.datetime).getTime();
      return filter === 'active' ? da - db : db - da;
    });
  }, [todos, filter]);

  const modalInitialValues = useMemo<Partial<TodoFormData>>(() => {
    if (editingTodo) {
      return {
        description: editingTodo.description,
        datetime: editingTodo.datetime,
        type: editingTodo.type,
        contact_id: editingTodo.contact_id,
        band_ids: editingTodo.band_ids,
      };
    }
    return selectedContactId ? { type: 'contact', contact_id: selectedContactId } : { type: 'contact' };
  }, [editingTodo, selectedContactId]);

  const openCreate = () => {
    setEditingTodo(null);
    setIsModalOpen(true);
  };

  const openEdit = (todo: Reminder) => {
    setEditingTodo(todo);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTodo(null);
  };

  const handleSubmit = (data: TodoFormData) => {
    const id = editingTodo?._id;
    run(
      () => (id ? todosAPI.update(id, data) : venuesAPI.addTodo(venueId, data)).then(() => invalidate()),
      { loading: id ? 'Saving todo...' : 'Creating todo...', success: id ? 'Todo saved' : 'Todo created' },
    );
  };

  const handleDone = (id: string) =>
    run(() => todosAPI.done(id).then(() => invalidate()), {
      loading: 'Marking as done...',
      success: 'Marked as done',
    });

  const handlePending = (id: string) =>
    run(() => todosAPI.update(id, { status: 'pending' }).then(() => invalidate()), {
      loading: 'Marking as pending...',
      success: 'Moved back to pending',
    });

  const handleDismiss = (id: string) =>
    run(() => todosAPI.dismiss(id).then(() => invalidate()), {
      loading: 'Dismissing...',
      success: 'Todo dismissed',
    });

  const handleSnooze = (id: string, days: number) =>
    run(() => todosAPI.snooze(id, days).then(() => invalidate()), {
      loading: 'Snoozing...',
      success: 'Todo snoozed',
    });

  return (
    <div className="flex flex-col gap-2 flex-shrink-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={toggleCollapsed}
            title={collapsed ? 'Show todos (t)' : 'Hide todos (t)'}
            aria-expanded={!collapsed}
            className="flex items-center gap-1 text-dark-text-primary hover:text-primary-400 transition-colors"
          >
            <ChevronDown
              className={clsx('w-3.5 h-3.5 transition-transform', collapsed && '-rotate-90')}
            />
            <h4 className="text-sm font-semibold">Todos ({filtered.length})</h4>
          </button>
          <button onClick={openCreate} title="Add todo" className={ACTION_BTN}>
            <Bell className="w-3.5 h-3.5" />
          </button>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as TodoFilter)}
          className="text-xs bg-dark-elevated border border-dark-border rounded-sm px-2 py-1 text-dark-text-primary"
        >
          <option value="active">Active</option>
          <option value="dismissed">Dismissed</option>
          <option value="done">Done</option>
          <option value="all">All</option>
        </select>
      </div>

      {!collapsed && (isLoading || filtered.length > 0) && (
        <div className="max-h-56 overflow-y-auto space-y-2">
          {isLoading ? (
            <div className="text-center py-2 text-dark-text-muted text-xs">Loading...</div>
          ) : (
            filtered.map((todo) => (
              <VenueTodoCard
                key={todo._id}
                todo={todo}
                onEdit={openEdit}
                onDone={handleDone}
                onPending={handlePending}
                onDismiss={handleDismiss}
                onSnooze={handleSnooze}
              />
            ))
          )}
        </div>
      )}

      <TodoModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        venueName={venueName}
        contacts={contacts}
        bands={bands}
        editMode={!!editingTodo}
        initialValues={modalInitialValues}
      />
    </div>
  );
}

interface VenueTodoCardProps {
  todo: Reminder;
  onEdit: (todo: Reminder) => void;
  onDone: (id: string) => void;
  onPending: (id: string) => void;
  onDismiss: (id: string) => void;
  onSnooze: (id: string, days: number) => void;
}

function VenueTodoCard({ todo, onEdit, onDone, onPending, onDismiss, onSnooze }: VenueTodoCardProps) {
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const Icon = TYPE_ICON[todo.type] ?? Bell;
  const date = new Date(todo.datetime);
  const isDone = todo.status === 'done';
  const isDismissed = todo.status === 'dismissed';
  const overdue = todo.status === 'pending' && isPast(date) && !isToday(date);
  const today = todo.status === 'pending' && isToday(date);

  return (
    <div className="p-2 rounded-lg text-xs border border-dark-border bg-dark-elevated">
      <div className="flex items-start gap-2">
        <Icon className="w-3.5 h-3.5 text-dark-text-muted flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p
            className={clsx(
              'font-medium wrap-break-word text-dark-text-primary',
              isDone && 'line-through opacity-60',
              isDismissed && 'line-through text-dark-text-muted',
            )}
          >
            {todo.description}
          </p>
          <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-0.5 text-dark-text-muted">
            <span
              className={clsx(
                'font-medium',
                overdue ? 'text-red-400' : today ? 'text-amber-400' : 'text-dark-text-secondary',
              )}
            >
              {format(date, 'MMM d, yyyy')}
            </span>
            <span>{format(date, 'h:mm a')}</span>
            {todo.contact_name && <span className="truncate">· {todo.contact_name}</span>}
            {todo.band_names && todo.band_names.length > 0 && (
              <span className="truncate">· {todo.band_names.join(', ')}</span>
            )}
            {isDone && <span className="px-1.5 py-0.5 rounded-sm bg-green-900/20 text-green-400">Done</span>}
            {isDismissed && (
              <span className="px-1.5 py-0.5 rounded-sm bg-dark-border text-dark-text-muted">Dismissed</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button onClick={() => onEdit(todo)} title="Edit" className={ACTION_BTN}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
          {isDone ? (
            <button onClick={() => onPending(todo._id)} title="Mark as pending" className={ACTION_BTN}>
              <Undo2 className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button onClick={() => onDone(todo._id)} title="Mark as done" className={ACTION_BTN}>
              <Check className="w-3.5 h-3.5" />
            </button>
          )}
          {todo.status === 'pending' && (
            <div className="relative">
              <button onClick={() => setSnoozeOpen((o) => !o)} title="Snooze" className={ACTION_BTN}>
                <Clock className="w-3.5 h-3.5" />
              </button>
              {snoozeOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setSnoozeOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-dark-surface border border-dark-border rounded-lg shadow-lg py-1 min-w-28">
                    {SNOOZE_PRESETS.map((p) => (
                      <button
                        key={p.days}
                        onClick={() => {
                          onSnooze(todo._id, p.days);
                          setSnoozeOpen(false);
                        }}
                        className="block w-full text-left px-3 py-1 text-xs text-dark-text-secondary hover:bg-dark-border hover:text-dark-text-primary"
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {!isDismissed && (
            <button
              onClick={() => onDismiss(todo._id)}
              title="Dismiss"
              className={clsx(ACTION_BTN, 'hover:text-red-400')}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
